import { useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useI18n } from '../../contexts/I18nContext';
import { WidgetFrame } from './WidgetFrame';
import { getWidgetDef, type LayoutWidget } from './widgets/widgetRegistry';

interface DashboardGridProps {
  widgets: LayoutWidget[];
  editing: boolean;
  /** Edit mode: a drag finished — move widget from one index to another. */
  onReorder?: (fromIndex: number, toIndex: number) => void;
  onCycleWidth?: (id: string) => void;
  onRemove?: (id: string) => void;
}

function GridCells({
  widgets,
  editing,
  onReorder,
  onCycleWidth,
  onRemove,
}: Pick<DashboardGridProps, 'widgets' | 'editing' | 'onReorder' | 'onCycleWidth' | 'onRemove'>) {
  return (
    <div className="grid grid-cols-12 gap-4">
      {widgets.map((widget, index) => {
        const def = getWidgetDef(widget.id);
        if (!def) return null; // sanitized layouts never hit this
        return (
          <WidgetFrame
            key={widget.id}
            widget={widget}
            def={def}
            editing={editing}
            onCycleWidth={onCycleWidth}
            onRemove={onRemove}
            onMoveUp={index > 0 ? () => onReorder?.(index, index - 1) : undefined}
            onMoveDown={
              index < widgets.length - 1 ? () => onReorder?.(index, index + 1) : undefined
            }
          />
        );
      })}
    </div>
  );
}

// Widgets have mixed col-spans, so dnd-kit's built-in sorting strategies
// (which assume equal-size items) produce garbage transforms across rows.
// Instead: no per-item transforms at all — we reorder the real list live in
// onDragOver and let the CSS grid reflow act as the (exact) drop preview.
const noSortingStrategy = () => null;

// Pointer-based targeting (the widget under the cursor) is stable with mixed
// sizes; closestCenter only as fallback for the keyboard sensor.
const collisionDetection: CollisionDetection = (args) => {
  const within = pointerWithin(args);
  return within.length > 0 ? within : closestCenter(args);
};

/**
 * The 12-column dashboard grid. View mode renders a plain CSS grid with zero
 * DnD mounted; edit mode wraps the same cells in a DndContext whose reorder
 * happens live during the drag (see noSortingStrategy above). The DragOverlay
 * ghost is a compact chip (icon + name); the faded original shows the slot
 * the widget will land in.
 */
export function DashboardGrid({
  widgets,
  editing,
  onReorder,
  onCycleWidth,
  onRemove,
}: DashboardGridProps) {
  const { t } = useI18n();
  const [activeId, setActiveId] = useState<string | null>(null);
  // Last `over` id already applied — prevents reorder oscillation when the
  // grid reflow puts a different widget under a stationary pointer.
  const lastOverId = useRef<string | null>(null);

  const sensors = useSensors(
    // 8px activation distance so plain clicks on the chrome buttons still work.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = useMemo(() => widgets.map((w) => w.id), [widgets]);

  if (!editing) {
    return <GridCells widgets={widgets} editing={false} />;
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    lastOverId.current = null;
  };

  // Live reorder: each time the drag hovers a new widget, move the dragged
  // widget to that slot. The drop itself has nothing left to do.
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const overId = String(over.id);
    if (lastOverId.current === overId) return;
    lastOverId.current = overId;
    const fromIndex = ids.indexOf(String(active.id));
    const toIndex = ids.indexOf(overId);
    if (fromIndex === -1 || toIndex === -1) return;
    onReorder?.(fromIndex, toIndex);
  };

  const handleDragEnd = () => {
    setActiveId(null);
    lastOverId.current = null;
  };

  const activeDef = activeId ? getWidgetDef(activeId) : undefined;
  const ActiveIcon = activeDef?.icon;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      // Items move mid-drag (live reorder), so rects must be re-measured or
      // subsequent collisions target stale positions.
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragEnd}
    >
      <SortableContext items={ids} strategy={noSortingStrategy}>
        <GridCells
          widgets={widgets}
          editing
          onReorder={onReorder}
          onCycleWidth={onCycleWidth}
          onRemove={onRemove}
        />
      </SortableContext>
      <DragOverlay>
        {activeDef && ActiveIcon ? (
          <div className="inline-flex cursor-grabbing items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-card-hover">
            <ActiveIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
            {t(activeDef.nameKey)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
